import { FormInput } from '@/components/form-input/form-input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Typo } from '@/components/ui/typography';
import { post } from '@/lib/axios';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePage } from '@inertiajs/react';
import { Mail, MapPin, Phone } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const newsletterSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
});

type NewsletterFormData = z.infer<typeof newsletterSchema>;

interface PageInfoItem {
  type?: string;
  value?: string;
}

export function NewsletterSection() {
  const page = usePage();
  const rawPageInfo = page.props.pageInfo;
  const form = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: NewsletterFormData) => {
    try {
      const response = await post('/api/newsletter/subscribe', data);
      if (response.success) {
        toast.success(response.message || 'Đăng ký nhận bản tin thành công!');
        form.reset();
      } else {
        toast.error(response.message || 'Đã có lỗi xảy ra. Vui lòng thử lại.');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Đã có lỗi xảy ra. Vui lòng thử lại.';
      toast.error(errorMessage);
      console.error('Newsletter subscription error:', error);
    }
  };

  const [address, phone, email] = useMemo(() => {
    // `getPageInfo()` deserialises to an object (not an array) whenever the stored
    // page_info has non-sequential keys, and it isn't shared outside the `web.inertia`
    // middleware group at all — so guard before treating it as a list.
    const items: PageInfoItem[] = Array.isArray(rawPageInfo) ? (rawPageInfo as PageInfoItem[]) : [];
    return [
      items.find((item) => item.type === 'address')?.value,
      items.find((item) => item.type === 'phone')?.value,
      items.find((item) => item.type === 'email')?.value,
    ];
  }, [rawPageInfo]);

  return (
    <section className="border-t border-gray-100 bg-white py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-6xl">
          <Card className="overflow-hidden rounded-2xl border-gray-100 py-0 shadow-sm">
            <div className="grid gap-0 md:grid-cols-2">
              {/* Left side - Image with overlay information */}
              <div className="bg-primary relative p-6 text-white md:p-12">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                  />
                </div>

                <div className="relative z-10 flex h-full flex-col justify-between">
                  <div>
                    <Typo variant="h3" className="mb-4 border-b-0 text-xl font-bold text-white md:text-3xl">
                      Nhận tin ưu đãi mới nhất
                    </Typo>
                    <Typo variant="muted" className="mb-8 text-white">
                      Đăng ký nhận bản tin để cập nhật những ưu đãi đặc biệt, điểm đến mới và xu hướng du lịch hấp dẫn nhất!
                    </Typo>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4 md:mt-8">
                    {phone && (
                      <div className="flex items-start gap-3">
                        <div className="mt-1 rounded-lg bg-white/20 p-2 backdrop-blur-sm">
                          <Phone className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <Typo variant="small" className="font-medium text-white">
                            Điện thoại
                          </Typo>
                          <p className="text-sm text-white">{phone}</p>
                        </div>
                      </div>
                    )}
                    {email && (
                      <div className="flex items-start gap-3">
                        <div className="mt-1 rounded-lg bg-white/20 p-2 backdrop-blur-sm">
                          <Mail className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <Typo variant="small" className="font-medium text-white">
                            Email
                          </Typo>
                          <p className="text-sm text-white">{email}</p>
                        </div>
                      </div>
                    )}

                    {address && (
                      <div className="flex items-start gap-3">
                        <div className="mt-1 rounded-lg bg-white/20 p-2 backdrop-blur-sm">
                          <MapPin className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <Typo variant="small" className="font-medium text-white">
                            Địa chỉ
                          </Typo>
                          <p className="text-sm text-white">{address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right side - Newsletter Form */}
              <div className="flex flex-col justify-center bg-white p-6 md:p-12">
                <div className="mb-6">
                  <Typo variant="h3" className="mb-2 text-2xl font-bold">
                    Đăng ký nhận thông tin khuyến mãi
                  </Typo>
                  {/* <Typo variant="muted" className="text-muted-foreground">Điền thông tin của bạn để nhận những cập nhật mới nhất</Typo> */}
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormInput
                      form={form}
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="Nhập email của bạn"
                      disabled={form.formState.isSubmitting}
                      isRequired
                    />

                    <Button
                      type="submit"
                      className="bg-primary w-full font-semibold text-white shadow-lg transition-all duration-200 hover:shadow-xl"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Đang xử lý...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Mail className="h-5 w-5" />
                          Đăng ký nhận tin
                        </span>
                      )}
                    </Button>

                    <p className="text-muted-foreground text-center text-xs">
                      Bằng cách đăng ký, bạn đồng ý với{' '}
                      <a href="/dieu-khoan-dich-vu/chinh-sach-bao-mat-va-xu-ly-du-lieu-ca-nhan" className="text-primary hover:underline">
                        Chính sách bảo mật
                      </a>{' '}
                      của chúng tôi
                    </p>
                  </form>
                </Form>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
